import { action, makeObservable, observable } from 'mobx';
import { BaseStore } from './baseStore';
import { RootStore } from './rootStore';
import { getApiUrl } from '/src/api';
import { BulkService } from '/src/api/services/bulk-service';
import { Nullable, UploadStatus } from '/src/lib/types';

export interface BulkStoreProps {
  /**
   * Whether to show the Export button
   */
  exportIsActive: boolean;
  /**
   * XMLHttpRequest object for the bulk upload request. Used so progress can be tracked.
   */
  xhr: XMLHttpRequest;
  /**
   * The current upload status
   */
  uploadStatus: UploadStatus;
  /**
   * Upload progress as a percentage
   */
  uploadProgress: number;
  /**
   * Whether the upload was successful
   */
  success: boolean;
  /**
   * Indicates the given file has the wrong name
   */
  wrongFile?: string;
  // fileObject?: any;
  // files: any[];
  /**
   * Import progress as a percentage
   */
  importProgressBar: number;
  /**
   * Queue of files to be uploaded
   */
  queue: Nullable<FileList | undefined>;
  /**
   * File import response message
   */
  returnMessage?: { saved: string[]; errors: string[] };
  /**
   * Whether an error occurred during import
   */
  error: boolean;
  /**
   * The error that occurred during import (if applicable)
   */
  errorMessage?: string;
}

export class BulkStore extends BaseStore implements BulkStoreProps {
  exportIsActive = false;
  xhr = new XMLHttpRequest();
  uploadStatus = UploadStatus.Ready;
  uploadProgress = 0;
  success = false;
  wrongFile?: string;
  // fileObject?: any;
  // files: any[] = [];
  importProgressBar = 1;
  queue: Nullable<FileList | undefined>;
  returnMessage?: { saved: string[]; errors: string[] };
  error = false;
  errorMessage?: string;

  constructor(rootStore: RootStore) {
    super(rootStore);

    makeObservable(this, {
      exportIsActive: observable,
      xhr: observable,
      uploadStatus: observable,
      uploadProgress: observable,
      success: observable,
      wrongFile: observable,
      // fileObject: observable,
      // files: observable,
      importProgressBar: observable,
      queue: observable,
      returnMessage: observable,
      error: observable,
      errorMessage: observable,
      updateFiles: action,
      upload: action,
      // uploadFiles: action,
    });
  }

  /**
   * Watch the file input and update the files array when it changes.
   *
   * @param files - List of changed files
   */
  updateFiles = (files: FileList) => {
    this.success = false;

    // Push files onto the uploads queue
    this.queue = files;

    if (this.importProgressBar === 1 && this.queue[0].name !== 'company.csv') this.wrongFile = 'company.csv';
    else if (this.importProgressBar === 2 && this.queue[0].name !== 'loan.csv') this.wrongFile = 'loan.csv';
    else if (this.importProgressBar === 3 && this.queue[0].name !== 'participation.csv')
      this.wrongFile = 'participation.csv';
    else if (this.importProgressBar === 4 && this.queue[0].name !== 'asset.csv') this.wrongFile = 'asset.csv';
    // else if (this.importProgressBar === 4 && this.queue[0].name !== 'asset.csv') Alerts.show('Please Import asset.csv');
    else if (this.importProgressBar === 5 && this.queue[0].name !== 'advance.csv') this.wrongFile = 'advance.csv';
    else if (this.importProgressBar === 6 && this.queue[0].name !== 'payment.csv') this.wrongFile = 'payment.csv';
    else {
      this.wrongFile = undefined;
      this.uploadStatus = UploadStatus.Queued;
    }

    if (this.uploadStatus === UploadStatus.Queued) this.upload();
  };

  /**
   * User-facing upload method.
   */
  upload = async () => {
    if (!this.rootStore.userStore.user) throw new Error('Not authorized');

    if (!this.queue?.[0]) return;

    const response = await BulkService.import(this.queue[0]);

    //   this.uploadFiles(this.queue[0], (returnObj) => {
    this.uploadStatus = UploadStatus.Done;
    this.returnMessage = JSON.parse(response);

    if (!this.returnMessage?.errors.length) {
      // Flag a successful upload
      this.success = true;
      this.error = false;

      // Advance the progress bar one step
      this.importProgressBar += 1;

      // Resets
      this.uploadProgress = 0;
      this.uploadStatus = UploadStatus.Ready;
    } else if (this.returnMessage.errors.length) {
      this.error = true;
    }
    //   });
  };

  /**
   * Low-level file upload method.
   *
   * @remarks
   * We're doing this manually instead of using `fetch` so that we can track the upload progress in real time.
   *
   * @param file - File to upload
   * @param callback - Called after upload
   */
  uploadFiles = (file: File, callback: (res: any) => void) => {
    this.uploadStatus = UploadStatus.Uploading;

    const xhr = this.xhr;
    const apiUrl = getApiUrl();

    // Track upload progress
    xhr.upload.onprogress = (upload) => (this.uploadProgress = (upload.loaded / upload.total) * 100);

    // Open connection & set headers
    xhr.open('POST', `${apiUrl}/import`, true);

    xhr.onload = () => {
      // Success callback
      if (xhr.status === 200) {
        this.success = true;
        this.error = false;
        this.errorMessage = undefined;
        callback(xhr.response);
      }

      // Forbidden error callback
      else if (xhr.status >= 400) {
        this.error = true;
        this.errorMessage = xhr.responseText;
        this.uploadStatus = UploadStatus.Ready;
        window.setTimeout(() => {
          this.error = false;
          this.errorMessage = undefined;
        }, 2000);
      }
    };

    // General error callback
    xhr.onerror = () => {
      this.success = false;
      this.error = true;
      this.errorMessage = 'An error has occurred';
      this.uploadStatus = UploadStatus.Ready;
    };

    // Engage
    xhr.send(file);
  };
}
